import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

// ----- Static catalogs (kept in frontend for fast rendering) -----
const MODEL_OPTIONS = [
  {
    value: 'gemini-3.1-flash-live-preview',
    label: 'Gemini Live 3.1 Flash (Preview)',
    description:
   'Native audio 3.1 paling baru. Most Recommended.'
  },
  {
    value: 'gemini-live-2.5-flash-preview',
    label: 'Gemini Live 2.5 Flash (Preview)',
    description:
      'Native audio — kualitas suara & nuansa percakapan bagus.',
  },
  {
    value: 'gemini-2.5-flash-preview-native-audio-dialog',
    label: 'Gemini 2.5 Flash Native Audio Dialog',
    description:
      'Native audio dialog yang dioptimalkan untuk percakapan dua-arah natural.',
  },
  {
    value: 'gemini-2.0-flash-live-001',
    label: 'Gemini 2.0 Flash Live (Stable)',
    description:
      'Versi stable half-cascade audio. Latency rendah dan biaya lebih hemat.',
  },
];

const VOICE_OPTIONS = [
  { value: 'Puck', description: 'Upbeat — ramah, cocok untuk friendly interviewer.' },
  { value: 'Charon', description: 'Informative — berwibawa, cocok untuk technical interviewer.' },
  { value: 'Kore', description: 'Firm — tegas, cocok untuk HR/formal interview.' },
  { value: 'Fenrir', description: 'Excitable — antusias, energik.' },
  { value: 'Aoede', description: 'Breezy — santai, casual.' },
  { value: 'Leda', description: 'Youthful — muda, cocok untuk audiens muda.' },
  { value: 'Orus', description: 'Firm — directive, mirip Kore tapi maskulin.' },
  { value: 'Zephyr', description: 'Bright — cerah dan upbeat narration.' },
  { value: 'Callirrhoe', description: 'Easy-going — relaks, percakapan panjang.' },
];

const DEFAULT_MODEL = 'gemini-live-2.5-flash-preview';
const DEFAULT_VOICE = 'Puck';

export const APISettingsTab = () => {
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [geminiModel, setGeminiModel] = useState(DEFAULT_MODEL);
  const [geminiVoice, setGeminiVoice] = useState(DEFAULT_VOICE);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      if (response.data.gemini_api_key) setGeminiApiKey(response.data.gemini_api_key);
      if (response.data.gemini_model) setGeminiModel(response.data.gemini_model);
      if (response.data.gemini_voice) setGeminiVoice(response.data.gemini_voice);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setFetching(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await api.post('/admin/settings', null, {
        params: {
          gemini_api_key: geminiApiKey,
          gemini_model: geminiModel,
          gemini_voice: geminiVoice,
        },
      });
      toast.success('Settings saved — applies to the next interview session (no restart needed).');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const selectedModel = MODEL_OPTIONS.find((m) => m.value === geminiModel);
  const selectedVoice = VOICE_OPTIONS.find((v) => v.value === geminiVoice);

  if (fetching) {
    return <p>Loading...</p>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-medium mb-4">API Configuration</h2>
        <p className="text-sm text-slate-500 mb-6">
          Configure your Gemini API key, live model, and voice. Changes apply to the
          <span className="font-medium"> next interview session</span> — no server restart required.
        </p>
      </div>

      <div className="space-y-6">
        {/* API KEY */}
        <div>
          <Label htmlFor="gemini-api-key">Gemini API Key</Label>
          <Input
            id="gemini-api-key"
            type="password"
            value={geminiApiKey}
            onChange={(e) => setGeminiApiKey(e.target.value)}
            placeholder="Enter your Gemini API key"
            data-testid="gemini-api-key-input"
            className="mt-2"
          />
          <p className="text-xs text-slate-500 mt-1">
            Get your API key from{' '}
            <a
              href="https://aistudio.google.com/app/apikey"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Google AI Studio
            </a>
          </p>
        </div>

        {/* MODEL */}
        <div>
          <Label htmlFor="gemini-model">Live Model</Label>
          <Select value={geminiModel} onValueChange={setGeminiModel}>
            <SelectTrigger
              id="gemini-model"
              className="mt-2"
              data-testid="gemini-model-select"
            >
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`model-option-${opt.value}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{opt.label}</span>
                    <span className="text-xs text-slate-500">{opt.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedModel && (
            <p className="text-xs text-slate-500 mt-1">
              <span className="font-medium text-slate-700">{selectedModel.value}</span> —{' '}
              {selectedModel.description}
            </p>
          )}
        </div>

        {/* VOICE */}
        <div>
          <Label htmlFor="gemini-voice">Interviewer Voice</Label>
          <Select value={geminiVoice} onValueChange={setGeminiVoice}>
            <SelectTrigger
              id="gemini-voice"
              className="mt-2"
              data-testid="gemini-voice-select"
            >
              <SelectValue placeholder="Select a voice" />
            </SelectTrigger>
            <SelectContent>
              {VOICE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`voice-option-${opt.value}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{opt.value}</span>
                    <span className="text-xs text-slate-500">{opt.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedVoice && (
            <p className="text-xs text-slate-500 mt-1">
              <span className="font-medium text-slate-700">{selectedVoice.value}</span> —{' '}
              {selectedVoice.description}
            </p>
          )}
        </div>

        {/* GCS INFO */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Google Cloud Storage</h4>
          <p className="text-sm text-blue-700">
            Video recordings are automatically stored in Google Cloud Storage. Configure your GCS
            bucket and credentials in the backend{' '}
            <code className="bg-blue-100 px-1 rounded">.env</code> file.
          </p>
          <p className="text-xs text-blue-600 mt-2">
            Bucket:{' '}
            <code className="bg-blue-100 px-1 rounded">
              {process.env.REACT_APP_GCS_BUCKET || 'interview-ai-videos'}
            </code>
          </p>
        </div>

        <Button onClick={saveSettings} disabled={loading} data-testid="save-settings-button">
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};
